// ═══════════════════════════════════════════════════════════════════════════
// commandDispatch/historyCommands.ts — Phase 4.1.k U / UNDO / UNDO BEGIN /
// UNDO END / UNDO BACK / REDO / MREDO CLI undo/redo router, extracted
// verbatim from App.tsx's `submitCommandText` (D5).
// ═══════════════════════════════════════════════════════════════════════════
//
// AutoCAD parity: U and UNDO undo the most recent action; REDO / MREDO redo
// the most recently undone action. Sub-commands UNDO BEGIN opens a named
// batch group; UNDO END commits it; UNDO BACK aborts it. UNDO MARK: places
// a marker (deferred to Phase 4.1.l — marker semantics differ from batch).
// CIS Russian aliases: ОТМ (U/UNDO), ПВТ (REDO/MREDO).
// ═══════════════════════════════════════════════════════════════════════════

import type { DispatchContext, DispatchResult } from "./types";

export function handleHistoryCommands(ctx: DispatchContext): DispatchResult {
  const {
    draftSession,
    resolvedCommandId,
    state,
    dispatch,
    historyBatchDepth,
    appendCommandFeed,
    t,
    setCommandText,
  } = ctx;

  if (draftSession) return null;

  if (resolvedCommandId === "UNDO-BEGIN") {
    // Phase 4.1.k iter-3 (Codex P2 #159) — sequential BEGIN ends prior
    // group then starts new. AutoCAD: BEGIN-while-open commits current.
    if (historyBatchDepth > 0) {
      dispatch({ type: "history/commit-batch", source: "commandline" });
    }
    dispatch({
      type: "history/begin-batch",
      source: "commandline",
      label: "UNDO BEGIN",
    });
    appendCommandFeed(t("feed.undo.mark"));
    setCommandText("");
    return true;
  }
  if (resolvedCommandId === "UNDO-END") {
    if (state.history.current === null) {
      appendCommandFeed(t("feed.undo.end.no-batch"));
      setCommandText("");
      return true;
    }
    // Phase 4.1.k iter-3 (Codex P2 #159) — differentiate nested-decrement
    // vs final-commit feed so user sees clear nesting level change.
    const wasNested = historyBatchDepth > 1;
    dispatch({ type: "history/commit-batch", source: "commandline" });
    appendCommandFeed(
      wasNested ? t("feed.undo.end.nested") : t("feed.undo.end.committed"),
    );
    setCommandText("");
    return true;
  }
  if (resolvedCommandId === "UNDO-BACK") {
    if (state.history.current === null) {
      appendCommandFeed(t("feed.undo.back.no-batch"));
      setCommandText("");
      return true;
    }
    dispatch({ type: "history/abort-batch", source: "commandline" });
    appendCommandFeed(t("feed.undo.back"));
    setCommandText("");
    return true;
  }
  if (resolvedCommandId === "UNDO") {
    // Phase 4.1.k iter-3 (Codex P1 #159) — refuse undo while batch open.
    // User must UNDO END or UNDO BACK first (AutoCAD parity).
    if (historyBatchDepth > 0) {
      appendCommandFeed(t("feed.undo.during-batch"));
      setCommandText("");
      return true;
    }
    if (state.history.past.length === 0) {
      appendCommandFeed(t("feed.undo.empty"));
      setCommandText("");
      return true;
    }
    dispatch({ type: "history/undo", source: "commandline" });
    appendCommandFeed(t("feed.undo"));
    setCommandText("");
    return true;
  }
  if (resolvedCommandId === "REDO") {
    // Phase 4.1.k iter-3 (Codex P1 #159) — refuse redo while batch open.
    if (historyBatchDepth > 0) {
      appendCommandFeed(t("feed.undo.during-batch"));
      setCommandText("");
      return true;
    }
    if (state.history.future.length === 0) {
      appendCommandFeed(t("feed.redo.empty"));
      setCommandText("");
      return true;
    }
    dispatch({ type: "history/redo", source: "commandline" });
    appendCommandFeed(t("feed.redo"));
    setCommandText("");
    return true;
  }

  return null;
}
